import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Modal } from '../modal/Modal';
import { Button } from '../primitives/Button';
import { TextButton } from '../primitives/TextButton';
import { hideToday, isHiddenToday } from './popupHideToday';
import { POPUP_EMPHASIS_CLASS, splitEmphasis } from './popupEmphasis';
import { groupByTitle } from './popupGrouping';
import { resolvePopupLinks } from './popupLinks';

/**
 * 호스트가 **실제로 렌더하는 칸만** 담은다 — 서버 `ActivePopupView`의 사본이 아니다.
 *
 * ⚠️ **이것이 계약을 좁게 두는 설계다.** 앱의 api 타입(web `ActivePopup` · admin 쪽 짝)은 서버가
 * 내려주는 칸을 그대로 담지만, 이 패키지는 그중 **그리는 데 필요한 셋**만 요구한다. 구조적 타이핑
 * 덕에 앱 타입이 그대로 대입되므로 매핑 보일러플레이트가 없고, 서버가 칸을 늘려도 이 선언은
 * 손댈 일이 없다(= `PopupFrequency` 같은 **두 번째 사본**이 여기 생기지 않는다).
 *
 * ⚠️ **2026-10-29 — `frequency`·`optOut`을 담기 시작했다.** 종전 이 자리는 「`frequency`를 일부러
 * 담지 않았다 — 두 버튼은 빈도와 무관하게 늘 보인다」였고 **그 전제가 뒤집혔다**(결정
 * `2026-10-26-popup-close-is-a-session-and-opt-out-is-an-option.md`의 ⓒ·개정 Q1~Q4): 확인 버튼의
 * **문구**가 `frequency`로 갈리고 opt-out **버튼의 종류**가 `optOut`으로 갈린다. 다시 뜰지는 여전히
 * **서버가 판정한다**(`shouldShowPopup` · 「다시 보지 않기」 기록). `contentRevisedAt`은 여전히 담지
 * 않는다 — 봤음 비교는 서버 몫이고 「오늘 하루」 키는 `id`만 쓴다.
 */
export interface PopupHostItem {
  id: string;
  title: string;
  body: string;
  /**
   * 재노출 — 확인 버튼 **문구만** 가른다(ⓒ: `ONCE` 「읽었습니다」 · `EVERY_VISIT` 「닫기」).
   * 생략·모르는 값은 `ONCE`로 읽는다(서버가 이 칸을 내려주기 전 번들·응답과 섞일 때).
   */
  frequency?: string;
  /**
   * opt-out — **값 하나에 버튼 하나**(개정 Q2). 뜻은 서버 `PopupOptOut` enum JSDoc이 정본이다.
   *
   * ⚠️ **생략이면 `HIDE_TODAY`로 읽는다** — 이 칸이 생기기 전 서버(배포 창)의 응답이 그 모양이고, 그때
   * 화면은 늘 「오늘 하루」를 그렸다(종전 동작을 지킨다). 모르는 값은 **`NONE`**이다 — 뜻을 모르는
   * 버튼을 그리지 않는 쪽이 안전하다(「확인」은 늘 있다).
   */
  optOut?: string;
  /**
   * 「전체 내역 보기」 링크 문구 — **서버가 소유한다**(운영자가 적거나 트리거가 넣는다).
   *
   * ⚠️ 목적지는 이 칸이 **아니다** — 세 갈래 계약과 그 근거는 `resolvePopupLinks` JSDoc이
   * 정본이다. 판정 응답이 이 칸을 **내려준다**(2026-10-26 서버 반영) — 자동 묶음 팝업은 이 칸이
   * 차 있고 `linkUrl`이 null이라 **앱이 주입한 목적지**로 간다.
   */
  linkLabel?: string | null;
  /**
   * 운영자가 적은 **실제 주소**. 자동 생성된 업데이트 묶음은 **null**이고, 그 null이
   * 「앱이 자기 목적지로 해소한다」는 **신호**다(= 소스 구분자를 따로 만들지 않는 이유).
   */
  linkUrl?: string | null;
}

export interface PopupHostProps {
  /**
   * 로그인 계정 id(web은 `users.id` · admin은 `super_admins.id`). **null이면 아무것도 띄우지 않는다** —
   * 「오늘 하루」 키가 계정 축이라(그 모듈 JSDoc) 계정 없이 키를 만들면 남이 누른 것이 내 것이 된다.
   *
   * ⚠️ **조회를 다시 트리거하는 유일한 값이다.** 조직 전환으로 재조회하지 않는 것이 맞다 —
   * 판정 스코프는 「내 **모든** 멤버십 ∪ 전사」이고(결정 2026-10-15 §B-ⓒ) 현재 선택 조직이 아니다.
   * 재조회를 넣으면 같은 후보를 다시 받아 **이미 닫은 팝업이 조직을 바꿀 때마다 다시 뜬다.**
   */
  viewerId: string | null;
  /** `GET /popups/active` — 앱의 api 클라이언트가 소유한다(이 패키지는 axios·토큰을 모른다) */
  fetchPopups: () => Promise<readonly PopupHostItem[]>;
  /** `POST /popups/:popupId/seen` — 봤음은 **계정 축**이라 서버가 소유한다 */
  markSeen: (popupId: string) => Promise<void>;
  /**
   * `POST /popups/:popupId/dismiss` — 「다시 보지 않기」. 봤음과 같은 **계정 축**이라 서버가 소유한다
   * (개정 Q2). ⚠️ **필수**인 이유: 생략 가능으로 두면 그 앱에서 `NEVER_AGAIN` 팝업의 버튼이 **조용히**
   * 안 보이거나(그리지 않음) 눌러도 아무 데도 기록되지 않는다(그리면) — 둘 다 red가 없다.
   */
  dismiss: (popupId: string) => Promise<void>;
  /**
   * 오버레이 z 층 — **여는 쪽이 주입한다**(모달 층 정책: 층은 컴포넌트가 아니라 여는 쪽 소유).
   * 생략하면 `Modal`의 기본값 `z-[70]`(= root 층)이다. 앱 열기 직후라 위에 다른 모달이 없으므로
   * 두 앱 모두 root가 맞다 — web은 `MODAL_LAYER.root` 상수를 넘겨 그 규약을 지키고(문자열을 박지
   * 않는다) admin은 그 상수 체계가 **없어서**(2026-10-26 census: `MODAL_LAYER` 0건) 생략한다.
   */
  overlayZClassName?: string;
  /**
   * 「전체 내역 보기」가 **이 앱에서** 갈 자리 — web `/release-notes` · admin `/release-note-feed`.
   *
   * ⚠️ **목적지를 앱이 소유하는 것이 계약이다**(ⓑ). 서버의 `linkUrl`은 surface별로 가를 수 없는
   * 한 칸이라 거기 들어가던 값이 **web 라우트**였고, admin에서 그 경로는 소비 화면이 아니라
   * 권한이 필요한 **관리 화면**이다 — 그대로 쓰면 대부분의 운영자가 막힌다. 세 갈래 전문은
   * `resolvePopupLinks` JSDoc이 정본이다.
   *
   * 생략하면 **라벨만 있는 팝업의 링크를 그리지 않는다**(앱이 목적지를 모르는데 서버 값으로
   * 떨어뜨리면 ⓐ의 함정으로 되돌아간다). 운영자가 주소를 적은 공지는 이 prop과 무관하게 뜬다.
   */
  appLinkHref?: string;
}

/** 호스트가 아는 opt-out 값 — 서버 `PopupOptOut`과 같은 셋이다(사본이지만 **렌더 분기의 분모**다) */
type HostOptOut = 'NONE' | 'HIDE_TODAY' | 'NEVER_AGAIN';

const KNOWN_OPT_OUTS: ReadonlySet<string> = new Set<HostOptOut>(['NONE', 'HIDE_TODAY', 'NEVER_AGAIN']);

/**
 * 한 장의 opt-out — **전부 같을 때만** 그 값이고, 섞이면 `NONE`(확인만)이다(호스트 JSDoc의 그 절).
 * 칸이 없으면 `HIDE_TODAY`(종전 동작) · 모르는 값이면 `NONE`이다(`PopupHostItem.optOut` JSDoc).
 */
function groupOptOut(items: readonly PopupHostItem[]): HostOptOut {
  const values = new Set(
    items.map((p): HostOptOut => {
      if (p.optOut === undefined) return 'HIDE_TODAY';
      return KNOWN_OPT_OUTS.has(p.optOut) ? (p.optOut as HostOptOut) : 'NONE';
    }),
  );
  return values.size === 1 ? [...values][0] : 'NONE';
}

/**
 * 일반 팝업 호스트 — 「지금 띄울 팝업」을 한 장씩 보여 준다
 * (줄기 `popup-and-announcement-surface` P2 · 결정 2026-10-15 §B).
 *
 * ⚠️ **web·admin 공용이다.** 종전에는 `apps/web/src/components/PopupHost.tsx`가 본체였고 admin에는
 * 팝업을 띄우는 코드가 **0건**이었다(2026-10-26 census) — 서버는 이미 슈퍼관리자 토큰을 이 라우트에
 * 받아 `ADMIN` surface로 판정하고 봤음을 제 칸(`PopupView.superAdminId`)에 쓰고 있었으므로
 * (`popups-viewer.controller.ts`의 그 docblock) **화면만 없었다.** 앱 의존(api 클라이언트·auth
 * store)은 전부 prop으로 가른다 — 이 패키지는 「공용 UI 프리미티브」이고 그 둘을 알아서는 안 된다.
 *
 * ## 띄우는 시점
 *
 * **로그인·앱 열기**다 — 두 앱의 `Layout` 안에 있으므로 인증된 사용자가 앱을 열 때 한 번
 * 마운트되고, 그때 한 번 조회한다. 화면 이동으로는 다시 뜨지 않는다(`Layout`이 계속 마운트돼 있다).
 * 사용자(계정)가 바뀔 때만 다시 조회한다 — `viewerId` prop JSDoc이 그 근거의 정본이다.
 *
 * ⚠️ pcon ADR-023 §4의 「창이 다시 보일 때·30분 간격 재판정」·「admin 즉시 노출 방송」은 **담지
 * 않았다** — 그쪽은 상주 데스크톱 앱을 위한 것이고 우리에겐 그 클라이언트가 없다(`DAILY` 빈도를
 * 뺀 것과 같은 이유). **되짚을 조건**: 상주 화면이 생기거나 「지금 바로 공지를 띄우고 싶다」는
 * 요구가 올라오면.
 *
 * ## 버튼은 «확인 하나 + opt-out 최대 하나»이고 각각 다른 축이다 (2026-10-29 개정)
 *
 * - **확인** — 서버에 봤음(계정 축). 문구가 `frequency`로 갈린다(ⓒ): `ONCE`면 「**읽었습니다**」
 *   (다시 안 뜬다 — 본문이 바뀔 때까지) · `EVERY_VISIT`이면 「**닫기**」(조건이 되면 다시 뜬다).
 *   ⛔ 한 문구로 고정하면 둘 중 하나에서 거짓이 된다 — 종전 「확인」이 그 상태였다.
 * - **opt-out** — 팝업의 `optOut` 값대로 **하나만**(`NONE`이면 0개 — 업데이트 내역이 그 쓰임이다):
 *   - 「오늘 하루 보지 않기」(`HIDE_TODAY`) — 브라우저 저장소(**기기 축**). ⚠️ 재노출 정책 「A-a」의
 *     단점을 그대로 떠안는다: 누르면 **그 뒤 추가분도 그날은 안 보인다**(결정 2026-10-14 ⓐ)
 *   - 「다시 보지 않기」(`NEVER_AGAIN`) — 서버 기록(**계정 축**) · 본문이 바뀌어도 다시 안 뜬다(Q3)
 *
 * ⚠️ 한 장에 팝업이 여럿이면 **opt-out 값이 전부 같을 때만** 그 버튼을 그린다 — 섞여 있으면 「이 장을
 * 어느 축으로 그만 보나」가 정해지지 않는다(그때는 확인만). 확인 문구는 **하나라도 `EVERY_VISIT`이면
 * 「닫기」**다(「읽었습니다」가 그 팝업에는 거짓이다). 오늘 실경로의 장은 업데이트 내역 묶음뿐이라
 * 늘 같은 값이다.
 *
 * ## 같은 제목은 한 장에 («장» 단위로 닫는다)
 *
 * 「같은 날짜면 동일 항목에」(2026-10-26 사용자 요구)를 **제목 동등 비교**로 구현한다 — 축과
 * 한계 둘(운영자가 같은 제목을 둘 만들면 합쳐진다 · 「같은 날짜 ≡ 같은 제목」은 제목을 날짜만으로
 * 만들기 때문인 암묵 계약이고 그 소유자는 `bundleTitle`이다)은 `groupByTitle` JSDoc이 정본이다.
 *
 * ⚠️ 그래서 **「확인」과 opt-out(「오늘 하루」·「다시 보지 않기」)이 장 단위로 동작한다** — pcon 10-3의 「『닫기』 한 번에 모두
 * 봤음」이다. 한 장에 담긴 팝업 **전부**에 봤음을 보내고 전부를 숨긴다. 하나만 처리하면 나머지가
 * 다음 앱 열기에 다시 떠서 사용자에게는 「닫았는데 또 뜬다」로 보인다.
 *
 * ⚠️ 종전 이 자리에는 **「합치지 않는 이유」**가 적혀 있었다(「소스 구분자가 응답에 없다」). 그
 * 판정은 **축을 「소비자 종류」로 잘못 잡은 것**이었다 — 요구의 축은 **날짜**이고 그것은 제목에
 * 이미 있다. ⇒ 서버 변경 0으로 풀렸다. 그 금지(요청자 쪽 `bundleSourceKey` docblock)는
 * **`sourceKey` 파싱**에 대한 것이고 제목 **동등 비교**에는 걸리지 않는다.
 *
 * ## 「전체 내역 보기」 링크 — 라벨은 서버가, 목적지는 앱이 소유한다 (ⓑ)
 *
 * 세 갈래(`linkUrl` 있으면 그 주소 · 없고 라벨만이면 **주입받은 `appLinkHref`** · 둘 다 없으면
 * 안 그린다)와 그 근거·한계는 `resolvePopupLinks` JSDoc이 정본이다 — 여기 옮겨 적지 않는다.
 *
 * ⚠️ **둘째 갈래는 2026-10-26부터 실경로다** — 서버가 그 두 칸을 응답에 실었고(`listActive`의
 * `.map`) 트리거가 `linkUrl`을 null로 두므로, 자동 묶음 팝업에 **링크가 실제로 뜬다.** 종전 이
 * 자리는 「응답에 없어 안 보이는 것이 정상」이라 적고 있었는데 **그 전제가 서버에서 바뀌었다** —
 * 낡은 것이 아니라 **뒤집힌** 서술이었다. 첫째 갈래(`linkUrl` 있음)는 **여전히 도달 불가**이고
 * 그 측정은 `resolvePopupLinks` JSDoc이 정본이다.
 *
 * 이 자리의 초록은 **「주입된 값이 그대로 쓰인다」**까지다 — 목적지는 `appLinkHref`로 **주입**되므로
 * 「그 값이 옳은 화면을 가리키나」의 소유자는 **각 앱의 배선**이다.
 *
 * ⭐ **그 둘째 축은 census로 닫혔다**(2026-10-26 · 2026-10-27 재측). ⛔ **「spec이 못 닫는다」를
 * 「브라우저가 아니면 못 닫는다」로 번역하지 말 것** — 주입된 값은 **리터럴 census + 라우트 표**를
 * 나란히 세면 닫힌다: 주입 리터럴(web `apps/web/src/components/PopupHost.tsx` · admin
 * `apps/admin/src/components/Layout.tsx`) ↔ 그 앱의 라우트(web `App.tsx`의 `/release-notes` ·
 * admin `App.tsx`의 `release-note-feed` — 부모가 `path="/"`라 같은 절대 경로로 해소된다).
 * 양쪽 다 일치했고 **[대조군]**으로 「다른 앱의 경로가 이 앱 라우트에 0건」을 함께 세어 계측기
 * 생존을 확인했다. ⇒ **두 계측기가 축을 나눠 덮는다**(spec = 값이 그려지나 · census = 목적지가
 * 옳은가). 다음 사람이 이 축을 「실측 대기」로 다시 미루지 말 것.
 */
export function PopupHost({
  viewerId,
  fetchPopups,
  markSeen,
  dismiss,
  overlayZClassName,
  appLinkHref,
}: PopupHostProps) {
  const [queue, setQueue] = useState<readonly PopupHostItem[]>([]);

  // ⚠️ 주입된 함수를 **ref로 들고** 효과의 의존에서 뺀다 — 소비처가 `fetchPopups={() => fetch()}`
  //    처럼 인라인 화살표를 넘기면 매 렌더마다 새 함수가 되어 **조회가 무한히 재실행**된다. 그
  //    실패는 조용하지 않지만(네트워크가 돈다) 원인이 이 파일처럼 보이지 않으므로 여기서 막는다.
  //    의존은 `viewerId` 하나이고, 그것이 「계정이 바뀔 때만 다시 조회한다」는 계약 그 자체다.
  const fetchRef = useRef(fetchPopups);
  const markSeenRef = useRef(markSeen);
  const dismissRef = useRef(dismiss);
  useEffect(() => {
    fetchRef.current = fetchPopups;
    markSeenRef.current = markSeen;
    dismissRef.current = dismiss;
  }, [fetchPopups, markSeen, dismiss]);

  useEffect(() => {
    if (!viewerId) {
      setQueue([]);
      return;
    }
    let alive = true;
    void (async () => {
      try {
        const popups = await fetchRef.current();
        if (!alive) return;
        // 「오늘 하루」는 기기 축이라 서버가 모른다 — 받은 뒤 여기서 거른다
        setQueue(popups.filter((p) => !isHiddenToday(viewerId, p.id)));
      } catch {
        // 팝업은 보조 표면이다 — 조회 실패로 화면을 막지 않는다
        if (alive) setQueue([]);
      }
    })();
    return () => {
      alive = false;
    };
  }, [viewerId]);

  // 제목이 같은 것끼리 한 장에 모은다 — 그 축과 한계는 `groupByTitle` JSDoc이 정본이다.
  const groups = useMemo(() => groupByTitle(queue), [queue]);
  const current = groups[0] ?? null;

  // 장에 든 항목들에서 그릴 링크 — 세 갈래 계약·중복 제거는 `resolvePopupLinks` JSDoc이 정본이다.
  // 자동 묶음 팝업은 라벨이 차 있어 **실제로 링크가 나온다**(2026-10-26 서버 반영 뒤).
  const links = useMemo(
    () => (current ? resolvePopupLinks(current.items, appLinkHref) : []),
    [current, appLinkHref],
  );

  /**
   * 지금 «장»을 통째로 뺀다 — 한 장이 팝업 여럿을 담으므로 `slice(1)`이 아니다.
   *
   * ⚠️ 제목으로 거르는 것이 맞다 — 그룹 키가 제목이므로 **같은 제목이 두 장에 나뉘는 일이
   * 구조적으로 없다**(`groupByTitle`이 한 제목당 한 장을 만든다). id 목록으로 걸러도 같지만
   * 그러면 「장 = 제목」이라는 불변식이 호출부에서 안 보인다.
   */
  const advance = useCallback((title: string) => {
    setQueue((prev) => prev.filter((p) => p.title !== title));
  }, []);

  const handleConfirm = useCallback(() => {
    if (!current) return;
    const ids = current.items.map((p) => p.id);
    advance(current.title);
    // 닫기를 먼저 반영한다 — 기록이 느리거나 실패해도 사용자가 기다리지 않는다.
    // 실패하면 다음 앱 열기에 다시 뜬다(공지가 유실되는 반대 방향보다 안전하다).
    //
    // ⚠️ **그 장에 담긴 전부에 보낸다** — pcon 10-3의 「『닫기』 한 번에 모두 봤음」이다. 하나만
    // 보내면 나머지는 다음 앱 열기에 **다시 뜨고**, 사용자에게는 「닫았는데 또 뜬다」로 보인다.
    for (const popupId of ids) {
      void markSeenRef.current(popupId).catch(() => undefined);
    }
  }, [current, advance]);

  const handleHideToday = useCallback(() => {
    if (!current || !viewerId) return;
    // 「오늘 하루」도 장 단위다 — 사용자가 누른 것은 「이 장을 오늘 그만」이다.
    for (const item of current.items) {
      hideToday(viewerId, item.id);
    }
    advance(current.title);
  }, [current, viewerId, advance]);

  /**
   * 「다시 보지 않기」 — 장 단위 · 계정 축. 닫기를 먼저 반영하고 기록은 뒤에 보낸다(`handleConfirm`과
   * 같은 이유 — 실패하면 다음 방문에 다시 뜨는 쪽이 안전한 실패다). 서버가 400을 내는 경우(운영자가
   * 그사이 opt-out을 바꿨다)도 여기서 삼킨다 — 그 팝업은 다음 방문에 새 값대로 다시 뜬다.
   */
  const handleDismiss = useCallback(() => {
    if (!current) return;
    const ids = current.items.map((p) => p.id);
    advance(current.title);
    for (const popupId of ids) {
      void dismissRef.current(popupId).catch(() => undefined);
    }
  }, [current, advance]);

  if (!current) return null;

  const optOut = groupOptOut(current.items);
  const confirmLabel = current.items.some((p) => p.frequency === 'EVERY_VISIT') ? '닫기' : '읽었습니다';

  // ⚠️ **닫는 길은 둘이 아니라 셋이다** — 위 「두 버튼이 «다른 축»이다」 절은 「확인」(계정 축)과
  // 「오늘 하루」(기기 축)만 말하는데, `Modal`의 `onClose`는 **Esc·오버레이 클릭·✕ 셋 다**에서
  // 불리고(`closeOnEsc`·`closeOnOverlay`·`showCloseButton` 전부 기본 true) 그 핸들러가
  // `handleConfirm`이라 **서버에 「봤음」을 쓴다**. 생성 기본값이 `ONCE`이므로
  // (`popups.service.ts`의 `dto.frequency ?? 'ONCE'`) `shouldShowPopup('ONCE', seen=true)`가 false고
  // ⇒ **한 번 닫히면 그 공지는 다시 뜨지 않는다.**
  // ⇒ 2026-10-19 사용자 결정: **오버레이 클릭만 막는다.** 우발적 클릭으로 공지를 영구히 놓치는 길만
  // 없애고, Esc와 ✕는 사람이 **의도해서** 누른 것이므로 「읽고 닫았다」로 그대로 둔다.
  // ⚠️ `EVERY_VISIT` 팝업은 이 축과 무관하다 — `shouldShowPopup`이 그 값에서 `seen`을 **안 본다**.
  // 그래서 이 결정이 바꾸는 것은 `ONCE`(= 기본값) 팝업뿐이다.
  return (
    <Modal
      open
      closeOnOverlay={false}
      onClose={handleConfirm}
      // ⚠️ **이 자리가 공용 `bodyScroll`을 가장 필요로 하는 형상이다** — 푸터가 있고(아래 두 버튼)
      // 오버레이 닫기가 **off**라, 본문이 길어지면 닫는 길 셋(Esc·✕·푸터 버튼) 중 **터치에서는 둘**
      // (✕·푸터)이 전부 화면 밖으로 밀린다. 그래서 「패널에 스크롤을 건다」는 선례를 베끼면
      // 막다른 길이 된다(패널이 스크롤되므로 도달은 가능하지만 **항상 보이는 탈출구가 없다**).
      // ⇒ `bodyScroll`은 본문만 스크롤하고 헤더·푸터를 고정하므로 두 버튼이 늘 보인다.
      //
      // ⚠️ **본문 길이에 상한을 두지 않기로 한 것이 이 한 줄에 기대고 있다** — 업데이트 내역 팝업은
      // 한 날 최대 61건을 담을 수 있고(중위 5 · p90 약 20), 그 축을 상한이 아니라 스크롤이 가져갔다.
      // 정본은 `docs/decisions/2026-10-26-release-note-popup-is-generated-by-the-notes-not-referenced-by-the-popup.md`의 ⓒ·ⓘ.
      bodyScroll
      title={current.title}
      size="md"
      overlayZClassName={overlayZClassName}
      footer={
        // opt-out이 없으면 확인만 오른쪽에 둔다 — `justify-between`의 왼쪽 칸이 비어도 위치가
        // 같도록 빈 자리를 남기지 않고 `justify-end`로 바꾼다
        <div
          className={`flex items-center gap-3 ${optOut === 'NONE' ? 'justify-end' : 'justify-between'}`}
        >
          {optOut === 'HIDE_TODAY' && (
            <TextButton type="button" onClick={handleHideToday}>
              오늘 하루 보지 않기
            </TextButton>
          )}
          {optOut === 'NEVER_AGAIN' && (
            <TextButton type="button" onClick={handleDismiss}>
              다시 보지 않기
            </TextButton>
          )}
          <Button type="button" onClick={handleConfirm}>
            {confirmLabel}
          </Button>
        </div>
      }
    >
      {/* 한 장에 묶음이 여럿일 수 있다 — **구분선으로 차례대로**(pcon 10-3). 하나면 구분선도
          여백도 없어 **종전 화면과 같다**(`index > 0`에만 선을 붙인다).

          ⚠️ key는 `item.id`다 — 인덱스로 쓰면 큐가 줄 때 React가 다른 묶음의 DOM을 재사용해
          본문이 섞일 수 있다(그 실패는 조용하고 콘솔 경고도 없다).

          줄바꿈은 본문이 소유한다 — `whitespace-pre-wrap`으로 그대로 보인다.
          `**강조**`만 가르고 마크다운·HTML은 해석하지 않는다(`splitEmphasis` JSDoc).
          강조 구간의 클래스는 `POPUP_EMPHASIS_CLASS`가 소유한다 — 굵게 **와 강조색**이고,
          그 색을 raw 팔레트로 두는 근거는 그 상수 JSDoc이 정본이다(여기 값을 박지 말 것). */}
      {current.items.map((item, index) => (
        <p
          key={item.id}
          className={`whitespace-pre-wrap break-words text-sm text-ink-700${
            index > 0 ? ' mt-4 border-t border-line pt-4' : ''
          }`}
        >
          {splitEmphasis(item.body).map((seg, i) =>
            seg.strong ? (
              <strong key={i} className={POPUP_EMPHASIS_CLASS}>
                {seg.text}
              </strong>
            ) : (
              <span key={i}>{seg.text}</span>
            ),
          )}
        </p>
      ))}

      {/* 「전체 내역 보기」 — 세 갈래 계약(`linkUrl` 있으면 그 주소 · 없고 라벨만이면 주입받은
          목적지 · 둘 다 없으면 안 그린다)과 중복 제거 근거는 `resolvePopupLinks` JSDoc이 정본이다.

          ⚠️ 자동 묶음 팝업에서는 **실제로 한 줄이 그려진다**(서버가 라벨을 실고 url을 null로 두므로
          주입받은 `appLinkHref`로 간다). 라벨이 없는 운영자 공지에서는 빈 배열이라 안 그린다.

          ⚠️ 버튼이 아니라 `<a>`다 — **이동**이고 액션이 아니다(`Button`·`TextButton`은 액션용).
          색은 `TextButton`의 brand 톤과 같은 램프를 쓴다(그 파일의 `TONE_CLASS`가 그 값의 정본). */}
      {links.map((link) => (
        <a
          key={link.key}
          href={link.href}
          className="mt-4 inline-block text-sm text-brand-600 hover:text-brand-800"
        >
          {link.label}
        </a>
      ))}
    </Modal>
  );
}
